import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import StatusChip from "components/shared/StatusChip";
import TextButton from "components/shared/TextButton";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "store/store";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import { ETransferTermSalesStatus } from "store/transferTermSalesStore";

import styles from "./style.module.scss";

/**
 * Table within TransferTermDrawer which displays all sales payments of term.
 */
const PaymentsTable = ({
    /**
     * Sales payments of the term.
     */
    termSales,
    /**
     * Rules for StatusChip.
     */
    salesStatusRules,
    /**
     * Triggers opening modal when clicking on action.
     */
    handleOpeningModal,
}: {
    termSales: ITransferTermSale[] | undefined;
    salesStatusRules: any;
    handleOpeningModal: any;
}) => {
    const [t] = useTranslation();

    const { termSalesStatus } = useAppSelector((state) => state.transferTerms);

    /**
     * Options for the button on the Action column: changes dynamically depending on the status of the sale.
     * @returns Object with text and action.
     **/
    const actionOptions = (sale: ITransferTermSale) => {
        switch (sale.status) {
            case ETransferTermSalesStatus.DUE_NEXT:
            case ETransferTermSalesStatus.WAITING_STATEMENT:
            case ETransferTermSalesStatus.LATE_STATEMENT:
                return {
                    text: t("TransferTerms.SalesTable.EnterRights"),
                    action: (e: any, sale) => handleOpeningModal(sale, "addRights", "new", e),
                };
            case ETransferTermSalesStatus.INVOICE_TO_ISSUE:
                return {
                    text: t("TransferTerms.SalesTable.ConfirmInvoice"),
                    action: (e: any, sale) => handleOpeningModal(sale, "addInvoice", "", e),
                };
            case ETransferTermSalesStatus.WAITING_PAYMENT:
            case ETransferTermSalesStatus.LATE_PAYMENT:
                return {
                    text: t("TransferTerms.SalesTable.SetAsPaid"),
                    action: (e: any, sale) => handleOpeningModal(sale, "payStatus", "new", e),
                };
            case ETransferTermSalesStatus.PAYED:
                return {
                    text: t("TransferTerms.SalesTable.UpdatePayment"),
                    action: (e: any, sale) => handleOpeningModal(sale, "payStatus", "edit", e),
                };
            default:
                return {
                    text: "-",
                    action: () => {},
                };
        }
    };

    const paymentColumns: LastElementTableColumns[] = [
        {
            id: "dueDate",
            label: t("TransferTerms.SalesTable.DueDate"),
            align: "left",
            format: (value) => formatLocaleDateString({ date: value }),
        },
        {
            id: "paymentDate",
            label: t("TransferTerms.SalesTable.PaymentDate"),
            align: "right",
            format: (value) => formatLocaleDateString({ date: value }),
        },
        {
            id: "grossAmount",
            label: t("TransferTerms.TermTable.RoyaltiesCreated"),
            align: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true }),
        },
        {
            id: "netAmount",
            label: t("TransferTerms.TermTable.RoyaltiesPaid"),
            align: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true }),
        },
        {
            id: "status",
            label: t("TransferTerms.TermTable.Status"),
            align: "left",
            format: (value: any, id, currentRow) => (
                <div className={styles["status-cell"]}>
                    <StatusChip status={value} statusRules={salesStatusRules} />
                </div>
            ),
        },
        {
            id: "mainAction",
            label: t("TransferTerms.SalesTable.Action"),
            align: "left",
            opacityReducer: "isDeleted",
            format: (value, id, currentRow) => {
                return (
                    <TextButton
                        text={actionOptions(currentRow)?.text ?? ""}
                        handleClick={(e) => actionOptions(currentRow)?.action(e, currentRow)}
                        underlineOnHover={!currentRow?.isDeleted}
                        style={{ textAlign: "left" }}
                        disabled={currentRow?.isDeleted}
                    />
                );
            },
        },
    ];

    return (
        <LastElementTable
            data={termSales}
            columns={paymentColumns}
            max={-1}
            isLoading={termSalesStatus === "pending"}
            dataType="transferTermSales"
        />
    );
};

export default PaymentsTable;
